import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import api from '../utils/api'
import Navbar from '../components/Navbar'

const STEPS = [
  { label: 'Account', icon: '1' },
  { label: 'Security', icon: '2' },
  { label: 'Scanning', icon: '3' },
  { label: 'Results', icon: '4' },
]

export default function SetupTFA() {
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { user, refreshUser } = useAuth()
  const navigate = useNavigate()
  const hasGenerated = useRef(false)

  // Check if we're in the registration flow (has scan subject stored)
  const isRegistrationFlow = !!(localStorage.getItem('scan_subject_name') || localStorage.getItem('scan_subject_email'))

  useEffect(() => {
    if (user?.tfa_enabled) {
      handlePostTFA()
      return
    }
    // Only generate once to avoid the loop
    if (!hasGenerated.current) {
      hasGenerated.current = true
      generateTFA()
    }
  }, [user, navigate])

  async function handlePostTFA() {
    const subjectName = localStorage.getItem('scan_subject_name')
    const subjectEmail = localStorage.getItem('scan_subject_email') || user?.email

    if (subjectName && subjectEmail) {
      // Auto-trigger free scan
      try {
        const response = await api.post('/snapshot', {
          name: subjectName,
          email: subjectEmail,
        })
        // Clean up
        localStorage.removeItem('scan_subject_name')
        localStorage.removeItem('scan_subject_email')
        // Redirect to scan progress
        navigate(`/snapshot/${response.data.id}`)
        return
      } catch {
        // If scan fails, just go to dashboard
      }
      localStorage.removeItem('scan_subject_name')
      localStorage.removeItem('scan_subject_email')
    }

    navigate('/dashboard')
  }

  async function generateTFA() {
    try {
      const response = await api.post('/auth/setup-tfa')
      setQrCode(response.data.qr_code)
      setSecret(response.data.secret)
    } catch (err: any) {
      // If 400, TFA is already set up - redirect to dashboard
      if (err.response?.status === 400) {
        await refreshUser()
        handlePostTFA()
        return
      }
      setError('Failed to generate TFA. Please refresh and try again.')
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await api.post('/auth/enable-tfa', { code })
      // Refresh user state so tfa_enabled is true
      await refreshUser()
      await handlePostTFA()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid code. Try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4 flex flex-col items-center justify-center min-h-screen">
        {/* Step Progress Bar — only show in registration flow */}
        {isRegistrationFlow && (
          <div className="w-full max-w-lg mb-8">
            <div className="flex items-center justify-between">
              {STEPS.map((step, i) => (
                <div key={step.label} className="flex items-center flex-1 last:flex-initial">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      i < 1
                        ? 'bg-gradient-to-br from-green-400 to-green-600 text-white'
                        : i === 1
                        ? 'bg-gradient-to-br from-ginger-400 to-orange-500 text-white shadow-lg shadow-ginger-500/40'
                        : 'bg-dark-700 text-dark-500'
                    }`}>
                      {i < 1 ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : step.icon}
                    </div>
                    <span className={`mt-2 text-xs font-medium ${
                      i < 1 ? 'text-green-400' : i === 1 ? 'text-ginger-400' : 'text-dark-500'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-3 mt-[-1rem] ${i < 1 ? 'bg-green-500' : 'bg-dark-700'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card max-w-lg w-full">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-ginger-500/20 mb-4">
              <span className="text-3xl">🔐</span>
            </div>
            <h1 className="text-2xl font-bold mb-2">Set Up Two-Factor Authentication</h1>
            <p className="text-dark-400">
              TFA is required for all accounts. This protects your sensitive data.
            </p>
          </div>

          <div className="space-y-6">
            {/* Step 1: Install App */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-ginger-500 flex items-center justify-center font-bold text-sm">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Install an authenticator app</h3>
                <p className="text-dark-400 text-sm">
                  Download Google Authenticator, Authy, or 1Password on your phone.
                </p>
              </div>
            </div>

            {/* Step 2: Scan QR */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-ginger-500 flex items-center justify-center font-bold text-sm">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Scan this QR code</h3>
                <p className="text-dark-400 text-sm mb-4">
                  Open your authenticator app and scan the code below.
                </p>

                {qrCode ? (
                  <div className="bg-white p-4 rounded-lg inline-block">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                  </div>
                ) : (
                  <div className="w-48 h-48 bg-dark-800 rounded-lg animate-pulse" />
                )}

                <details className="mt-4">
                  <summary className="text-dark-400 text-sm cursor-pointer hover:text-dark-300">
                    Can't scan? Enter code manually
                  </summary>
                  <code className="block mt-2 p-3 bg-dark-800 rounded text-sm break-all">
                    {secret || 'Loading...'}
                  </code>
                </details>
              </div>
            </div>

            {/* Step 3: Verify */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-ginger-500 flex items-center justify-center font-bold text-sm">
                3
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Enter the 6-digit code</h3>
                <p className="text-dark-400 text-sm mb-4">
                  Enter the code shown in your authenticator app to verify setup.
                </p>

                <form onSubmit={handleVerify} className="space-y-4">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="input text-center text-2xl tracking-widest"
                    maxLength={6}
                    required
                  />

                  {error && <p className="text-red-400 text-sm">{error}</p>}

                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="btn btn-primary w-full"
                  >
                    {loading ? 'Verifying...' : 'Enable Two-Factor Authentication'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-dark-700">
            <p className="text-dark-500 text-xs text-center">
              Save your backup codes in a secure location. If you lose access to your authenticator,
              you'll need these to recover your account.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
